import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../context/UserContext";
import { fetchCallCounts, fetchMultipleCongressMembers, logMessageOutcome } from "../../../utils/takeActionApi";
import { verifyUrl } from "../../../utils/issueReportApi";
import ReportIssueAction from "../../AlertComponents/ReportIssueAction";
import AddressEntryForm from "../../AddressEntryForm";
import {
  lookupPublicRepsByAddress,
  normalizedRepsToTakeActionMembers,
} from "../../../utils/repLookupUtils";

function getPartyColor(party) {
  if (!party) return "#64748b";
  const p = String(party).toLowerCase();
  if (p.includes("democrat") || p === "d") return "#2563eb";
  if (p.includes("republican") || p === "r") return "#dc2626";
  if (p.includes("independent") || p === "i") return "#7c3aed";
  return "#64748b";
}

function getPartyAbbrev(party) {
  if (!party) return "";
  const p = String(party).toLowerCase();
  if (p.includes("democrat") || p === "d") return "D";
  if (p.includes("republican") || p === "r") return "R";
  if (p.includes("independent") || p === "i") return "I";
  return String(party).charAt(0).toUpperCase();
}

function formatOffice(member) {
  return [
    member?.office_address,
    member?.office_address_city,
    member?.office_address_zipcode,
  ]
    .filter(Boolean)
    .join(", ");
}

function normalizeMember(member, role) {
  return {
    ...member,
    role,
    bioguide_id: member?.bioguide_id ?? member?.bioguideId,
    phone: member?.phone_number || member?.phone || "Not available",
    contact_form:
      member?.official_website_contact_url || member?.contact_form || "Not available",
    official_website_url: member?.official_website_url || "Not available",
    office: formatOffice(member) || "Not available",
  };
}

function telHref(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits.length >= 10 ? `tel:${digits}` : null;
}

const MEMBER_ACTION_BTN_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.75,
  px: 2,
  py: 1,
  minHeight: 44,
  minWidth: 180,
  borderRadius: "12px",
  border: "none",
  bgcolor: "#047857",
  color: "#fff",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  textDecoration: "none",
  whiteSpace: "nowrap",
  boxShadow: "none",
  "&:hover": {
    bgcolor: "#065f46",
    color: "#fff",
    boxShadow: "none",
  },
  "& .MuiButton-startIcon": {
    color: "#fff",
    margin: 0,
    marginRight: "6px",
  },
};

function MemberCard({ member, role, district, callCount = 0, showCallCount = true, onWritePress }) {
  const partyColor = getPartyColor(member?.party);
  const partyAbbrev = getPartyAbbrev(member?.party);
  const memberPrefix = role === "senator" ? "Sen." : role === "delegate" ? "Del." : "Rep.";
  const callLabel = callCount === 0 ? "No calls" : callCount === 1 ? "1 call" : `${callCount} calls`;
  const hasPhone = member?.phone && member.phone !== "Not available";
  const phoneHref = hasPhone ? telHref(member.phone) : null;

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        p: 2,
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mb: showCallCount ? 1 : 0,
            }}
          >
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#0f172a", lineHeight: 1.25 }}>
              {memberPrefix} {member.first_name} {member.last_name}
            </Typography>
            {partyAbbrev ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  backgroundColor: partyColor,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 700,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                {partyAbbrev}
              </Box>
            ) : null}
            {district ? (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                <LocationOnOutlinedIcon
                  sx={{ fontSize: 24, color: "#475569" }}
                  aria-hidden
                />
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#475569", lineHeight: 1 }}>
                  {district}
                </Typography>
              </Box>
            ) : null}
          </Box>

          {showCallCount ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: "999px",
                bgcolor: callCount > 0 ? "rgba(4, 120, 87, 0.12)" : "#f1f5f9",
              }}
            >
              <PhoneInTalkIcon
                sx={{ fontSize: 14, color: callCount > 0 ? "#047857" : "#64748b" }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: callCount > 0 ? "#047857" : "#64748b",
                }}
              >
                {callLabel}
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexShrink: 0,
            alignItems: "stretch",
          }}
        >
          {hasPhone ? (
            phoneHref ? (
              <Link
                href={phoneHref}
                underline="none"
                sx={MEMBER_ACTION_BTN_SX}
              >
                <PhoneInTalkOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} aria-hidden />
                {member.phone}
              </Link>
            ) : (
              <Box sx={MEMBER_ACTION_BTN_SX}>
                <PhoneInTalkOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} aria-hidden />
                {member.phone}
              </Box>
            )
          ) : (
            <Box
              sx={{
                ...MEMBER_ACTION_BTN_SX,
                bgcolor: "#cbd5e1",
                color: "#fff",
                "&:hover": { bgcolor: "#cbd5e1" },
              }}
            >
              <PhoneInTalkOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} aria-hidden />
              No phone listed
            </Box>
          )}

          <Button
            variant="contained"
            onClick={onWritePress}
            startIcon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={MEMBER_ACTION_BTN_SX}
          >
            Write
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default function TakeActionModal({
  open,
  onClose,
  billType,
  billNumber,
  billTitle,
  billCongress,
}) {
  const navigate = useNavigate();
  const {
    user,
    userRepsBioguideID,
    userSenOneBioguideID,
    userSenTwoBioguideID,
    userState,
    userDistrict,
    userFirstName,
    userLastName,
    addressSkipped,
  } = useUser();

  // Logged-out visitors look up their reps by address inside this same modal.
  const [guestMembers, setGuestMembers] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const isLoggedOut = !user;
  const isGuest = Boolean(
    isLoggedOut && guestMembers && (guestMembers.rep || guestMembers.senOne || guestMembers.senTwo)
  );
  const effectiveState = isGuest ? guestMembers?.state : userState;
  const effectiveDistrict = isGuest ? guestMembers?.district : userDistrict;

  const [members, setMembers] = useState({ rep: null, senOne: null, senTwo: null });
  const [callCounts, setCallCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [writeMode, setWriteMode] = useState(false);
  const [writingTo, setWritingTo] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [copiedSubject, setCopiedSubject] = useState(false);
  const [copiedBody, setCopiedBody] = useState(false);
  const [messageLogging, setMessageLogging] = useState(false);
  const [messageLogged, setMessageLogged] = useState(false);

  const [contactFormStatus, setContactFormStatus] = useState(null); // null | "checking" | verify result object

  const billSlug = useMemo(() => {
    if (!billType || !billNumber) return null;
    return `${String(billType).toLowerCase()}-${String(billNumber).toLowerCase()}`;
  }, [billType, billNumber]);

  const memberCount = [members.rep, members.senOne, members.senTwo].filter(Boolean).length;
  const isDCResident = effectiveState?.toUpperCase() === "DC";

  const getBillDisplay = useCallback(() => {
    if (billType && billNumber) {
      return `${String(billType).toUpperCase()} ${billNumber}${billTitle ? ` - ${billTitle}` : ""}`;
    }
    return "[Bill Number]";
  }, [billType, billNumber, billTitle]);

  const resetSubView = useCallback(() => {
    setWriteMode(false);
    setWritingTo(null);
    setCopiedSubject(false);
    setCopiedBody(false);
    setMessageLogged(false);
    setMessageLogging(false);
    setContactFormStatus(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetSubView();
      setError(null);
      setCallCounts({});
      setLookupError("");
      return undefined;
    }

    // Guests come from the address lookup, which only returns name/party/phone.
    // Hydrate the full public member records so they get contact forms too.
    // Call counts stay out of it — that endpoint requires auth.
    if (isGuest) {
      const repRole = isDCResident ? "delegate" : "representative";
      const buildMembers = (memberMap = {}) => ({
        rep: guestMembers.rep
          ? normalizeMember(memberMap[guestMembers.rep.bioguide_id] || guestMembers.rep, repRole)
          : null,
        senOne: guestMembers.senOne
          ? normalizeMember(memberMap[guestMembers.senOne.bioguide_id] || guestMembers.senOne, "senator")
          : null,
        senTwo: guestMembers.senTwo
          ? normalizeMember(memberMap[guestMembers.senTwo.bioguide_id] || guestMembers.senTwo, "senator")
          : null,
      });

      setError(null);
      setCallCounts({});
      setMembers(buildMembers());

      const bioguideIds = [guestMembers.rep, guestMembers.senOne, guestMembers.senTwo]
        .map((member) => member?.bioguide_id)
        .filter(Boolean);
      if (bioguideIds.length === 0) {
        setLoading(false);
        return undefined;
      }

      let active = true;
      setLoading(true);
      fetchMultipleCongressMembers(bioguideIds)
        .then((memberMap) => {
          if (active) setMembers(buildMembers(memberMap));
        })
        .catch(() => {
          // Keep the lookup data — phone numbers still work without hydration.
        })
        .finally(() => {
          if (active) setLoading(false);
        });

      return () => {
        active = false;
      };
    }

    if (!user) return undefined;

    let active = true;
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const bioguideIds = [
          userRepsBioguideID,
          userSenOneBioguideID,
          userSenTwoBioguideID,
        ].filter(Boolean);

        if (bioguideIds.length === 0) {
          if (active) {
            setMembers({ rep: null, senOne: null, senTwo: null });
            setCallCounts({});
          }
          return;
        }

        const [memberMap, countsData] = await Promise.all([
          fetchMultipleCongressMembers(bioguideIds),
          fetchCallCounts(bioguideIds, billSlug),
        ]);

        if (!active) return;

        setCallCounts(countsData || {});
        setMembers({
          rep: userRepsBioguideID
            ? normalizeMember(memberMap[userRepsBioguideID], "representative")
            : null,
          senOne: userSenOneBioguideID
            ? normalizeMember(memberMap[userSenOneBioguideID], "senator")
            : null,
          senTwo: userSenTwoBioguideID
            ? normalizeMember(memberMap[userSenTwoBioguideID], "senator")
            : null,
        });
      } catch (err) {
        if (active) {
          setError(err?.message || "Failed to load representative information.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadData();
    return () => {
      active = false;
    };
  }, [
    open,
    user,
    isGuest,
    guestMembers,
    isDCResident,
    userRepsBioguideID,
    userSenOneBioguideID,
    userSenTwoBioguideID,
    billSlug,
    resetSubView,
  ]);

  const buildEmailTemplate = useCallback(
    (member) => {
      const displayName = isGuest
        ? "[Your Name]"
        : [userFirstName, userLastName].filter(Boolean).join(" ") || "[Your Name]";
      const stateDistrict = isGuest
        ? [effectiveState, effectiveDistrict].filter((v) => v != null && v !== "").join("-") || "[State-District]"
        : [userState, userDistrict].filter(Boolean).join("-") || "[State-District]";
      const billDisplay = getBillDisplay();
      const memberTitle = member?.role === "senator" ? "Senator" : "Representative";
      const billUrl = billSlug
        ? `https://www.civixthread.com/bills/${billSlug}`
        : "https://www.civixthread.com/";

      return {
        subject: `${displayName} (${stateDistrict}) — re: ${billDisplay}`.substring(0, 140),
        body: [
          `Dear ${memberTitle} ${member?.last_name || "[Last Name]"},`,
          "",
          `I'm writing regarding ${billDisplay}. I [support/oppose] this legislation because [brief reason].`,
          "",
          "Thank you for your service to our community and for considering my perspective.",
          "",
          "Sincerely,",
          displayName,
          stateDistrict,
          "—",
          `This bill on CivixThread: ${billUrl}`,
        ].join("\n"),
      };
    },
    [billSlug, effectiveDistrict, effectiveState, getBillDisplay, isGuest, userDistrict, userFirstName, userLastName, userState]
  );

  const handleWritePress = useCallback(
    (member) => {
      const template = buildEmailTemplate(member);
      setSubject(template.subject);
      setBody(template.body);
      setWritingTo(member);
      setWriteMode(true);
      setCopiedSubject(false);
      setCopiedBody(false);
      setMessageLogged(false);
      setMessageLogging(false);

      const formUrl = member?.contact_form;
      if (formUrl && formUrl !== "Not available") {
        setContactFormStatus("checking");
        verifyUrl(formUrl).then((result) => {
          setContactFormStatus(result);
        });
      } else {
        setContactFormStatus(null);
      }
    },
    [buildEmailTemplate]
  );

  const handleCopyText = useCallback(async (value, setter) => {
    try {
      await navigator.clipboard.writeText(value || "");
      setter(true);
      window.setTimeout(() => setter(false), 2000);
    } catch {
      setError("Failed to copy to clipboard.");
    }
  }, []);

  const handleMarkMessageSent = useCallback(async () => {
    if (!writingTo || messageLogging || messageLogged) return;
    if (isGuest) {
      setError("Create a free account to track your outreach.");
      return;
    }
    const bioguideId = writingTo?.bioguide_id || writingTo?.bioguideId;
    if (!bioguideId) {
      setError("Could not determine which representative you messaged.");
      return;
    }

    setMessageLogging(true);
    setError(null);
    try {
      await logMessageOutcome({
        billId: billSlug,
        billType,
        billNumber,
        repBioguideId: bioguideId,
      });
      setMessageLogged(true);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not log your message right now. Please try again.");
    } finally {
      setMessageLogging(false);
    }
  }, [billSlug, billType, billNumber, isGuest, messageLogged, messageLogging, writingTo]);

  const handleGuestAddressLookup = useCallback(async (addressPayload) => {
    setLookupLoading(true);
    setLookupError("");
    try {
      const normalized = await lookupPublicRepsByAddress(addressPayload);
      const isDC = normalized.state?.toUpperCase() === "DC";
      const nextMembers = normalizedRepsToTakeActionMembers(normalized, isDC);
      if (!nextMembers.rep && !nextMembers.senOne && !nextMembers.senTwo) {
        setGuestMembers(null);
        setLookupError("We couldn't find representatives for that address. Please check it and try again.");
        return;
      }
      setGuestMembers(nextMembers);
    } catch (err) {
      setGuestMembers(null);
      setLookupError(err?.message || "Could not find representatives for that address.");
    } finally {
      setLookupLoading(false);
    }
  }, []);

  const handleHeaderBack = () => {
    if (writeMode) {
      resetSubView();
      return;
    }
    onClose();
  };

  const headerTitle = writeMode ? "Write to Your Rep" : "Contact Your Representatives";

  const renderAddressPrompt = () => (
    <Box sx={{ textAlign: "center", py: 5, px: 2 }}>
      <MapOutlinedIcon sx={{ fontSize: 48, color: "#047857", mb: 2 }} />
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: "#0f172a", mb: 1 }}>
        Add Your Address
      </Typography>
      <Typography sx={{ fontSize: 16, color: "#0f172a", lineHeight: 1.6, mb: 3 }}>
        To contact your representatives, we need your address to determine your congressional district.
      </Typography>
      <Box sx={{ textAlign: "left", maxWidth: 480, mx: "auto", mb: 3 }}>
        <Typography sx={{ fontSize: 15, color: "#0f172a", mb: 1.25 }}>
          Direct contact info for all 3 members
        </Typography>
        <Typography sx={{ fontSize: 15, color: "#0f172a", mb: 1.25 }}>
          Pre-written message templates
        </Typography>
        <Typography sx={{ fontSize: 15, color: "#0f172a" }}>
          Email templates you can copy and send
        </Typography>
      </Box>
      <Button
        variant="contained"
        onClick={() => {
          onClose();
          navigate("/account-details");
        }}
        sx={{
          py: 1.5,
          px: 4,
          borderRadius: "12px",
          fontWeight: 700,
          backgroundColor: "#047857",
          "&:hover": { backgroundColor: "#065f46" },
        }}
      >
        Add Address
      </Button>
    </Box>
  );

  const renderGuestSignupPrompt = () => (
    <Box
      sx={{
        p: 2,
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        backgroundColor: "#f8fafc",
        textAlign: "center",
      }}
    >
      <Box
        component="p"
        sx={{
          m: 0,
          mb: 1.5,
          fontSize: { xs: 12.5, sm: 13.5 },
          fontWeight: 800,
          color: "#0f172a",
          lineHeight: 1.35,
          fontFamily: "inherit",
        }}
      >
        Create an account for a personalized civic dashboard that makes taking action easier.
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          justifyContent: "center",
          alignItems: "stretch",
        }}
      >
        <Button
          variant="contained"
          fullWidth
          onClick={() => {
            onClose();
            navigate("/auth/signup");
          }}
          sx={{
            borderRadius: "12px",
            px: 2.5,
            py: 1.1,
            fontWeight: 700,
            textTransform: "none",
            backgroundColor: "#047857",
            "&:hover": { backgroundColor: "#065f46" },
          }}
        >
          Create account
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => {
            onClose();
            navigate("/features");
          }}
          sx={{
            borderRadius: "12px",
            px: 2.5,
            py: 1.1,
            fontWeight: 700,
            textTransform: "none",
            borderColor: "#047857",
            color: "#047857",
            "&:hover": {
              borderColor: "#065f46",
              color: "#065f46",
              backgroundColor: "rgba(4, 120, 87, 0.06)",
            },
          }}
        >
          Check out features
        </Button>
      </Box>
    </Box>
  );

  const renderGuestAddressSection = () => (
    <Box
      sx={{
        backgroundColor: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        p: 2,
        mb: 2.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <LocationOnOutlinedIcon sx={{ color: "#047857" }} />
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
          {isGuest ? "Using a different address?" : "Find your representatives"}
        </Typography>
      </Box>
      <AddressEntryForm
        hint="We use your address only to determine your congressional district. Nothing is saved."
        hintColor="#0f172a"
        submitLabel="Find my representatives"
        onSubmit={handleGuestAddressLookup}
        loading={lookupLoading}
        externalError={lookupError}
        submitAlign="center"
      />
    </Box>
  );

  const renderMemberSelection = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#047857" }} />
          <Typography sx={{ mt: 2, color: "#0f172a" }}>
            Loading your representatives...
          </Typography>
        </Box>
      );
    }

    if (user && addressSkipped && memberCount === 0) {
      return renderAddressPrompt();
    }

    return (
      <Box sx={{ py: 1 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569", mb: 0.25 }}>
            Regarding
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a", lineHeight: 1.4 }}>
            {getBillDisplay()}
          </Typography>
        </Box>

        {isLoggedOut ? renderGuestAddressSection() : null}

        {members.rep ? (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a", mb: 1.25 }}>
              {isDCResident ? "Delegate" : "House Representative"}
            </Typography>
            <MemberCard
              member={members.rep}
              role={isDCResident ? "delegate" : "representative"}
              district={
                effectiveDistrict != null && effectiveDistrict !== ""
                  ? `${effectiveState || ""}-${effectiveDistrict}`
                  : effectiveState || null
              }
              callCount={callCounts[members.rep?.bioguide_id] || 0}
              showCallCount={!isGuest}
              onWritePress={() => handleWritePress(members.rep)}
            />
          </Box>
        ) : null}

        {isDCResident && !members.senOne && !members.senTwo ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            D.C. residents have a non-voting Delegate and no U.S. Senators.
          </Alert>
        ) : null}

        {(members.senOne || members.senTwo) ? (
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a", mb: 1.25 }}>
              Senators
            </Typography>
            {members.senOne ? (
              <MemberCard
                member={members.senOne}
                role="senator"
                district={effectiveState || null}
                callCount={callCounts[members.senOne?.bioguide_id] || 0}
                showCallCount={!isGuest}
                onWritePress={() => handleWritePress(members.senOne)}
              />
            ) : null}
            {members.senTwo ? (
              <MemberCard
                member={members.senTwo}
                role="senator"
                district={effectiveState || null}
                callCount={callCounts[members.senTwo?.bioguide_id] || 0}
                showCallCount={!isGuest}
                onWritePress={() => handleWritePress(members.senTwo)}
              />
            ) : null}
          </Box>
        ) : null}

        {isLoggedOut ? (
          <Box sx={{ mt: 2.5 }}>
            {renderGuestSignupPrompt()}
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderWriteMode = () => (
    <Box sx={{ py: 1 }}>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "flex",
          backgroundColor: "#fff",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #e5e7eb",
          mb: 2,
        }}
      >
        <Box sx={{ p: 2, flex: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a", mb: 0.25 }}>
            Writing to
          </Typography>
          <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>
            {writingTo?.role === "senator" ? "Sen." : writingTo?.role === "delegate" ? "Del." : "Rep."} {writingTo?.first_name}{" "}
            {writingTo?.last_name}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: "#fff",
          p: 2,
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          mb: 2.5,
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", mb: 1 }}>
          How to send your message
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#0f172a", mb: 0.75 }}>
          1. Edit the message below to reflect your views
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#0f172a", mb: 1.5 }}>
          2. Copy and paste into their official contact form
        </Typography>
        {writingTo?.contact_form && writingTo.contact_form !== "Not available" ? (
          <>
            <Button
              variant="contained"
              startIcon={<OpenInNewRoundedIcon />}
              onClick={() =>
                window.open(writingTo.contact_form, "_blank", "noopener,noreferrer")
              }
              sx={{
                borderRadius: "999px",
                px: 2,
                py: 1,
                fontWeight: 700,
                backgroundColor: "#047857",
                "&:hover": { backgroundColor: "#065f46" },
              }}
            >
              Open Contact Form
            </Button>
            {contactFormStatus &&
              contactFormStatus !== "checking" &&
              !contactFormStatus.ok &&
              contactFormStatus.reason !== "inconclusive" && (
                <Box sx={{ mt: 1.5 }}>
                  <ReportIssueAction
                    issueType="broken_link"
                    url={writingTo.contact_form}
                    source="TakeActionModal"
                    description={`Contact form for ${writingTo?.first_name || ""} ${writingTo?.last_name || ""} (${writingTo?.role || "rep"}) returned ${contactFormStatus.reason || "error"}`}
                    warningText="This contact form link may be unavailable."
                    buttonLabel="Submit report to CivixThread"
                  />
                </Box>
              )}
          </>
        ) : null}
      </Box>

      <Box
        sx={{
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          p: 1.5,
          mb: 2,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: 14, color: "#0f172a", mb: 1, lineHeight: 1.5 }}>
          {isGuest
            ? "Create a free account to track your outreach and skip the address lookup next time."
            : "After you send through their contact form, tap below to log your outreach."}
        </Typography>
        <Button
          variant="contained"
          onClick={
            isGuest
              ? () => {
                  onClose();
                  navigate("/auth/signup");
                }
              : handleMarkMessageSent
          }
          disabled={!isGuest && (messageLogging || messageLogged)}
          sx={{
            borderRadius: "12px",
            px: 2.5,
            py: 1.1,
            fontWeight: 700,
            backgroundColor: "#047857",
            "&:hover": { backgroundColor: "#065f46" },
          }}
        >
          {isGuest ? (
            "Create free account"
          ) : messageLogging ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : messageLogged ? (
            "Thanks — message logged"
          ) : (
            "I sent my message"
          )}
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
            Subject
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleCopyText(subject, setCopiedSubject)}
            startIcon={<ContentCopyRoundedIcon />}
            sx={{
              minWidth: 0,
              px: 1.5,
              py: 0.75,
              borderRadius: "10px",
              fontWeight: 700,
              backgroundColor: "#047857",
              "&:hover": { backgroundColor: "#065f46" },
            }}
          >
            Copy
          </Button>
        </Box>
        <TextField
          fullWidth
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          inputProps={{ maxLength: 140 }}
          placeholder="Subject line"
        />
        <Typography sx={{ mt: 0.75, fontSize: 12, color: "#475569" }}>
          {subject.length}/140
        </Typography>
        {copiedSubject ? (
          <Typography sx={{ mt: 0.5, fontSize: 12, color: "#047857", fontWeight: 700 }}>
            ✓ Subject copied
          </Typography>
        ) : null}
      </Box>

      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
              Message
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#475569" }}>
              Complete [support/oppose] before sending
            </Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleCopyText(body, setCopiedBody)}
            startIcon={<ContentCopyRoundedIcon />}
            sx={{
              minWidth: 0,
              px: 1.5,
              py: 0.75,
              borderRadius: "10px",
              fontWeight: 700,
              backgroundColor: "#047857",
              "&:hover": { backgroundColor: "#065f46" },
            }}
          >
            Copy
          </Button>
        </Box>
        <TextField
          fullWidth
          multiline
          minRows={10}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Your message"
        />
        {copiedBody ? (
          <Typography sx={{ mt: 0.75, fontSize: 12, color: "#047857", fontWeight: 700 }}>
            ✓ Message copied
          </Typography>
        ) : null}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(15, 23, 42, 0.45)" } },
        paper: {
          sx: {
            width: "min(760px, 96vw)",
            maxHeight: "92vh",
            borderRadius: "12px",
            boxShadow: "none",
            border: "1px solid #e5e7eb",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#fff",
        }}
      >
        <IconButton onClick={handleHeaderBack} sx={{ color: "#0f172a" }}>
          <ArrowBackRoundedIcon />
        </IconButton>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
          {headerTitle}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#0f172a" }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          backgroundColor: "#fff",
          overflowY: "auto",
        }}
      >
        {writeMode ? renderWriteMode() : renderMemberSelection()}
      </DialogContent>
    </Dialog>
  );
}
